import {
  FaxContactDTO,
  FaxContactDTOPaginatedResponseDTO,
  FaxViewDTO,
  GetApiEFaxGetFaxContactsParams,
  MultiMediaResponseDTO,
  None,
  PatientMultiMediaResponseDTO,
  PostApiEFaxSetInboundFaxInternalStatusParams,
  SendFaxDTO,
  SettingsDTO,
  UploadToPatientChartDTO,
} from 'dtos';
import {
  UseMutationResult,
  UseQueryResult,
  keepPreviousData,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import {
  getMultiMediaByFaxId,
  resendFax,
} from 'pages/Dashboard/pages/Fax/transport';
import {
  PostApiEFaxSetInboundFaxInternalStatusResult,
  deleteApiEFaxDeleteFaxContact,
  getApiSettingsGetEFaxFilterSettings,
  getApiToDoGetFaxToDos,
  getApiEFaxGetFaxContacts,
  postApiEFaxSendFax,
  postApiEFaxSetInboundFaxInternalStatus,
  postApiEFaxUpdateFaxContact,
  postApiEFaxAddFaxContact,
  postApiEFaxUploadInboundFileToPatientChart,
} from 'endpoints';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';
import {
  normalizeFilterableSettings,
} from 'core/Filters/util';
import {
  ResendFaxParams,
} from 'pages/Dashboard/pages/Fax/types';
import {
  ToDo,
} from 'types/toDo';
import {
  normalizeToDos,
} from 'pages/Dashboard/pages/Todos/normalize';

export function useUploadInboundFileToPatientChart():
  UseMutationResult<PatientMultiMediaResponseDTO, Error, UploadToPatientChartDTO> {
  return (
    useMutation<PatientMultiMediaResponseDTO, Error, UploadToPatientChartDTO>({
      mutationFn: (data) => postApiEFaxUploadInboundFileToPatientChart(data),
    })
  );
}

export function useGetMediaByFaxId(
  faxId: number,
  enabled: boolean,
): UseQueryResult<MultiMediaResponseDTO, Error> {
  return useQuery<MultiMediaResponseDTO, Error>({
    queryKey: ['fax-media', faxId],
    queryFn: () => getMultiMediaByFaxId(faxId),
    enabled,
  });
}

export function useGetFaxToDos(
  faxId: number,
): UseQueryResult<ToDo[], Error> {
  return useQuery<ToDo[], Error>({
    queryKey: ['faxToDos', faxId],
    queryFn: async ({ signal }) => {
      const response = await getApiToDoGetFaxToDos({ faxId }, { signal });
      return normalizeToDos(response);
    },
  });
}

export function useResendFax(): UseMutationResult<FaxViewDTO, Error, ResendFaxParams> {
  return useMutation<FaxViewDTO, Error, ResendFaxParams>({
    mutationFn: resendFax,
  });
}

export function useSendFax(): UseMutationResult<None, Error, SendFaxDTO> {
  return useMutation<None, Error, SendFaxDTO>({
    mutationFn: (data) => postApiEFaxSendFax(data),
  });
}

export function useContacts(
  searchText: string,
  page: number,
  pageSize: number,
  enabled: boolean,
): UseQueryResult<FaxContactDTOPaginatedResponseDTO, Error> {
  return useQuery<FaxContactDTOPaginatedResponseDTO, Error>({
    queryKey: ['contacts', searchText, page, pageSize],
    queryFn: () => getApiEFaxGetFaxContacts({
      searchText: searchText?.trim() ?? '',
      page,
      pageSize,
    } as GetApiEFaxGetFaxContactsParams),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useUpdateFaxStatusHandler():
  UseMutationResult<
  PostApiEFaxSetInboundFaxInternalStatusResult,
  Error,
  PostApiEFaxSetInboundFaxInternalStatusParams
  > {
  return useMutation<
  PostApiEFaxSetInboundFaxInternalStatusResult,
  Error,
  PostApiEFaxSetInboundFaxInternalStatusParams>({
    mutationFn: (params) => postApiEFaxSetInboundFaxInternalStatus(params),
  });
}

export function useAddContact():
  UseMutationResult<FaxContactDTO, Error, Partial<FaxContactDTO>> {
  return useMutation<FaxContactDTO, Error, Partial<FaxContactDTO>>({
    mutationFn: (contact) => postApiEFaxAddFaxContact(contact),
  });
}

export function useUpdateContact():
  UseMutationResult<FaxContactDTO, Error, Partial<FaxContactDTO>> {
  return useMutation<FaxContactDTO, Error, Partial<FaxContactDTO>>({
    mutationFn: (contact) => postApiEFaxUpdateFaxContact(contact as FaxContactDTO),
  });
}

export function useDeleteContact():
  UseMutationResult<FaxContactDTO, Error, { faxContactId: number }> {
  return useMutation<FaxContactDTO, Error, { faxContactId: number }>({
    mutationFn: (params) => deleteApiEFaxDeleteFaxContact(params) as Promise<FaxContactDTO>,
  });
}

export function useGetEFaxFilterMetaComponents() {
  const { data } = usePrefetchedData<SettingsDTO>({
    key: ['eFax-settings'],
    fetchFn: getApiSettingsGetEFaxFilterSettings,
  });
  return normalizeFilterableSettings(data?.filterableSettings ?? []);
}
